import { tourAnchorIds } from '@ardoq/product-tours';
import { withBasePath } from 'basePath';
import {
  APICurrentUserOrganizations,
  ArdoqId,
  OrgAccessLevel,
} from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import { colors } from '@ardoq/design-tokens';
import { IconName, SvgImageName } from '@ardoq/icons';
import { NEW_PAGE_TOPBAR_HEIGHT, PAGE_TOPBAR_HEIGHT } from '@ardoq/page-layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { AppModules } from 'appContainer/types';
import type { SidebarScope } from 'appContainer/sidebarScope$';
import { logout, openChatWithUs } from 'components/AppMainSidebar/utils';
import { TasksStreamShape } from 'tasks/stream/tasksTypes';
import {
  CommonMenuItemProps,
  MenuItemExternalLinkProps,
  MenuItemProps,
  MenuItemRightContent,
} from '@ardoq/sidebar-menu';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import { closeNotificationsMenu, openNotificationsMenu } from './actions';
import { SidebarState } from './navigationTopBar$';
import { navigationTopBarCommands } from './navigationTopBarCommands';
import { NotificationsMenuProps } from './NotificationsMenu';
import {
  ChecksCounter,
  InAppNotificationsState,
  NotificationsFilterType,
} from '../streams/inAppNotifications/types';
import { filterSelected } from '../streams/inAppNotifications/actions';
import {
  NotificationCardProps,
  notificationOperations,
} from '@ardoq/in-app-notifications';
import {
  PermissionContext,
  permissionsOperations,
  resourcePermissionsOperations,
} from '@ardoq/access-control';
import { trackingData } from 'tracking/trackPayload';
import { trackEventV2 } from 'tracking/tracking';
import { orgAdminsListActions } from 'admin/orgAdminsList/actions';
import { openSearchModal } from 'searchModal/streams/actions';
import { toLinkProps } from 'router/declarative/toLinkProps';
import { accessControlRoutes } from 'admin/accessControl/declarativeRouter/consts';
import { toMetamodelHomeLocation } from 'organizationMetamodel/toMetamodelHomeLocation';
import { TextColor } from '@ardoq/typography';

export type ActiveMenuItem = AppModules;

type HideableMenuItem = {
  shouldHide?: boolean;
} & MenuItemProps;

export const openSearch = (sidebarState: SidebarState) => {
  trackEventV2({
    event: 'Sidebar0 Menu item clicked',
    payload: { label: 'Search', sidebarState },
  });
  dispatchAction(openSearchModal({ source: 'search-sidebar-button' }));
};

const setOrganization = (label: string) => {
  document.location.href = `/api/switchOrg?org=${label}`;
};

type GetTopSectionOptionsProps = {
  activeMenuItem?: ActiveMenuItem;
  orgHasDiscoverInCore: boolean;
  hasFoundationHome: boolean;
  hasTransformationsFeature: boolean;
  hasContentAwareBundleCopy: boolean;
  todoTasks: TasksStreamShape['todoTasks'];
  shouldShowFullCoreMenu: boolean;
  showMyTasksInNav: boolean;
  sidebarState: SidebarState;
  hasUnreadNotifications: boolean;
  isNotificationsMenuOpen: boolean;
  i18nLocale: I18nLocale;
};

export const getTopSectionOptions = ({
  activeMenuItem,
  orgHasDiscoverInCore,
  hasFoundationHome,
  hasTransformationsFeature,
  hasContentAwareBundleCopy,
  todoTasks,
  shouldShowFullCoreMenu,
  showMyTasksInNav,
  sidebarState,
  hasUnreadNotifications,
  isNotificationsMenuOpen,
  i18nLocale,
}: GetTopSectionOptionsProps): MenuItemProps[] => {
  const homeModules: ActiveMenuItem[] = shouldShowFullCoreMenu
    ? [AppModules.HOME]
    : [AppModules.DISCOVER_LANDING_PAGE];
  return [
    {
      label: t(i18nLocale, 'navigationTopBar:home', 'Home'),
      iconName: IconName.HOME,
      isActive: activeMenuItem && homeModules.includes(activeMenuItem),
      ...toLinkProps(
        { route: shouldShowFullCoreMenu ? '/home' : '/discover', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Home', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:notifications', 'Notifications'),
      iconName: hasUnreadNotifications
        ? IconName.NOTIFICATIONS_WITH_DOT
        : IconName.NOTIFICATIONS,
      isActive: isNotificationsMenuOpen,
      onClick: () => {
        if (isNotificationsMenuOpen) {
          dispatchAction(closeNotificationsMenu());
        } else {
          dispatchAction(
            openNotificationsMenu({
              [trackingData]: {
                event: 'Sidebar0 Menu item clicked',
                payload: { label: 'Notifications', sidebarState },
              },
            })
          );
        }
        if (sidebarState === 'hover') {
          navigationTopBarCommands.closeSidebar();
        }
      },
    },
    {
      label: t(i18nLocale, 'navigationTopBar:tasks', 'Tasks'),
      iconName: IconName.MY_TASKS,
      isActive: activeMenuItem === AppModules.TASKS,
      counter: todoTasks ? todoTasks.length : undefined,
      ...toLinkProps(
        { route: '/tasks', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Tasks', sidebarState },
          },
        }
      ),
      shouldHide:
        !orgHasDiscoverInCore || (!shouldShowFullCoreMenu && !showMyTasksInNav),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:solutions', 'Solutions'),
      iconName: IconName.SOLUTIONS,
      isActive:
        activeMenuItem ===
        (hasContentAwareBundleCopy
          ? AppModules.SOLUTIONS
          : AppModules.USE_CASES),
      ...(hasContentAwareBundleCopy
        ? toLinkProps(
            { route: '/solutions', params: {} },
            {
              trackingEvent: {
                event: 'Sidebar0 Menu item clicked',
                payload: { label: 'Solutions', sidebarState },
              },
            }
          )
        : toLinkProps(
            { route: '/use-cases', params: {} },
            {
              trackingEvent: {
                event: 'Sidebar0 Menu item clicked',
                payload: { label: 'Solutions', sidebarState },
              },
            }
          )),
      shouldHide: !shouldShowFullCoreMenu,
    },
    {
      label: t(i18nLocale, 'navigationTopBar:transformations', 'Transform'),
      iconName: IconName.PATH,
      isActive: activeMenuItem === AppModules.TRANSFORMATIONS,
      ...toLinkProps(
        { route: '/transformations', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Transformations', sidebarState },
          },
        }
      ),
      shouldHide: !hasTransformationsFeature || !shouldShowFullCoreMenu,
    },
    {
      label: t(i18nLocale, 'navigationTopBar:assetLibrary', 'Asset library'),
      iconName: IconName.FOLDER_LINE,
      isActive: activeMenuItem === AppModules.ASSET_LIBRARY,
      ...toLinkProps(
        { route: '/asset-library', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Asset library', sidebarState },
          },
        }
      ),
      shouldHide: !shouldShowFullCoreMenu || !hasFoundationHome,
    },
  ].filter((option: HideableMenuItem) => !option.shouldHide);
};

type GetInsightsOptionsProps = {
  activeMenuItem?: ActiveMenuItem;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
};
export const getInsightsOptions = ({
  activeMenuItem,
  sidebarState,
  i18nLocale,
}: GetInsightsOptionsProps): MenuItemProps[] => {
  return [
    {
      label: t(i18nLocale, 'navigationTopBar:dashboards', 'Dashboards'),
      iconName: IconName.DASHBOARD,
      isActive: activeMenuItem === AppModules.DASHBOARDS,
      ...toLinkProps(
        { route: '/dashboard', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Dashboards', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:reports', 'Reports'),
      iconName: IconName.REPORTS,
      isActive: activeMenuItem === AppModules.REPORTS,
      ...toLinkProps(
        { route: '/report', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Reports', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:presentations', 'Presentations'),
      iconName: IconName.PRESENTATION,
      isActive: activeMenuItem === AppModules.PRESENTATIONS,
      ...toLinkProps(
        { route: '/presentation-admin', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Presentations', sidebarState },
          },
        }
      ),
    },
  ];
};

// Discover scope shows the contributor view to everyone, whatever their role.
export const getShouldShowFullCoreMenu = (
  permissionContext: PermissionContext,
  sidebarScope: SidebarScope
): boolean =>
  sidebarScope !== 'discover' &&
  Boolean(
    permissionContext.user &&
    permissionsOperations.hasRequiredOrgAccessLevel(
      OrgAccessLevel.READER,
      permissionContext.user
    )
  );

export type DataInventoryNavGate = {
  shouldShowFullCoreMenu: boolean;
  showDataInventoryInNav: boolean;
};

export const hasDataInventoryInNav = ({
  shouldShowFullCoreMenu,
  showDataInventoryInNav,
}: DataInventoryNavGate): boolean =>
  shouldShowFullCoreMenu || showDataInventoryInNav;

type GetDataOptionsProps = {
  hasWriteAccess: boolean;
  activeMenuItem?: ActiveMenuItem;
  canAdminBroadcasts: boolean;
  shouldShowFullCoreMenu: boolean;
  showDataInventoryInNav: boolean;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
};
export const getDataOptions = ({
  hasWriteAccess,
  activeMenuItem,
  canAdminBroadcasts,
  shouldShowFullCoreMenu,
  showDataInventoryInNav,
  sidebarState,
  i18nLocale,
}: GetDataOptionsProps): MenuItemProps[] => {
  return [
    {
      label: t(i18nLocale, 'navigationTopBar:dataInventory', 'Data Inventory'),
      iconName: IconName.INVENTORY,
      isActive: activeMenuItem === AppModules.INVENTORY,
      dataTourId: tourAnchorIds.sidebarDataInventory,
      ...toLinkProps(
        { route: '/inventory/overview', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Inventory', sidebarState },
          },
        }
      ),
      shouldHide: !hasDataInventoryInNav({
        shouldShowFullCoreMenu,
        showDataInventoryInNav,
      }),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:surveys', 'Surveys'),
      iconName: IconName.SURVEYS,
      isActive: activeMenuItem === AppModules.SURVEY_ADMIN,
      ...toLinkProps(
        { route: '/survey-admin', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Surveys', sidebarState },
          },
        }
      ),
      shouldHide: !shouldShowFullCoreMenu,
    },
    {
      label: t(i18nLocale, 'navigationTopBar:broadcasts', 'Broadcasts'),
      iconName: IconName.BROADCAST,
      isActive: activeMenuItem === AppModules.BROADCASTS,
      ...toLinkProps(
        { route: '/broadcasts', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Broadcasts', sidebarState },
          },
        }
      ),
      shouldHide: !canAdminBroadcasts || !shouldShowFullCoreMenu,
    },
    {
      label: t(
        i18nLocale,
        'navigationTopBar:importsIntegrations',
        'Imports & Integrations'
      ),
      iconName: IconName.INTEGRATION,
      isActive: activeMenuItem === AppModules.INTEGRATIONS,
      ...toLinkProps(
        { route: '/integrations', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Imports & Integrations', sidebarState },
          },
        }
      ),
      shouldHide: !hasWriteAccess || !shouldShowFullCoreMenu,
    },
  ].filter((option: HideableMenuItem) => !option.shouldHide);
};

type GetVisualizationToolsOptionsProps = {
  activeMenuItem?: ActiveMenuItem;
  hasMetamodelFeature: boolean;
  shouldShowFullCoreMenu: boolean;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
};
export const getVisualizationToolsOptions = ({
  activeMenuItem,
  hasMetamodelFeature,
  shouldShowFullCoreMenu,
  sidebarState,
  i18nLocale,
}: GetVisualizationToolsOptionsProps): MenuItemProps[] => {
  if (!shouldShowFullCoreMenu) return [];
  return [
    {
      label: t(i18nLocale, 'navigationTopBar:viewpoints', 'Viewpoints'),
      iconName: IconName.VIEWPOINT,
      isActive: activeMenuItem === AppModules.VIEWPOINTS,
      ...toLinkProps(
        { route: '/viewpoints', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Viewpoints', sidebarState },
          },
        }
      ),
    },
    {
      label: t(
        i18nLocale,
        'navigationTopBar:metamodelPreviews',
        'Metamodel previews'
      ),
      iconName: IconName.METAMODEL,
      isActive: activeMenuItem === AppModules.METAMODEL,
      ...toLinkProps(
        { route: '/metamodel', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Metamodel Previews', sidebarState },
          },
        }
      ),
      shouldHide: !hasMetamodelFeature,
    },
  ].filter((option: HideableMenuItem) => !option.shouldHide);
};

type GetOrganizationDropdownOptionsProps = {
  activeMenuItem?: ActiveMenuItem;
  hasUsageMetrics: boolean;
  organizationName: string;
  organizationId?: ArdoqId;
  orgAccessLevel: OrgAccessLevel;
  canCreateGraphFilter: boolean;
  doesUserHaveAccessToFullCoreApp: boolean;
  currentUserOrganizations: APICurrentUserOrganizations[];
  hasMetamodelFeature: boolean;
  permissionContext: PermissionContext;
  hasAccessToSupportChat: boolean;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
};

export type MenuItemDropdownOption = CommonMenuItemProps & {
  onClick?: () => void;
  rightContent?: Extract<MenuItemRightContent, { type: 'new' }>;
  type?: DropdownOptionType;
  subMenu?: MenuItemDropdownOption[];
  iconColor?: string;
  color?: TextColor;
};

const toOrgMenuItem =
  (sidebarState: SidebarState) =>
  (org: APICurrentUserOrganizations): MenuItemDropdownOption => ({
    label: org.name,
    iconName: IconName.COMPONENT,
    ...(org.sandbox && { iconColor: colors.iconWarning }),
    onClick: () => {
      trackEventV2({
        event: 'Sidebar0 Menu item clicked',
        payload: { label: 'Switched organization', sidebarState },
      });
      setOrganization(org.label);
    },
  });

export const buildOrganizationsSubMenu = (
  organizations: APICurrentUserOrganizations[],
  sidebarState: SidebarState,
  i18nLocale: I18nLocale
): MenuItemDropdownOption[] => {
  const regularOrgs = organizations.filter(org => !org.sandbox);
  const sandboxOrgs = organizations.filter(org => org.sandbox);

  const items: MenuItemDropdownOption[] = regularOrgs.map(
    toOrgMenuItem(sidebarState)
  );

  if (sandboxOrgs.length > 0) {
    if (regularOrgs.length > 0) {
      items.push({ label: '', type: DropdownOptionType.DIVIDER });
    }
    items.push({
      label: t(i18nLocale, 'navigationTopBar:sandbox', 'SANDBOX'),
      type: DropdownOptionType.HEADER,
      color: 'textWarning',
    });
    items.push(...sandboxOrgs.map(toOrgMenuItem(sidebarState)));
  }

  return items;
};

export const getOrganizationDropdownOptions = ({
  activeMenuItem,
  hasUsageMetrics,
  organizationName,
  organizationId,
  orgAccessLevel,
  canCreateGraphFilter,
  doesUserHaveAccessToFullCoreApp,
  currentUserOrganizations,
  permissionContext,
  hasAccessToSupportChat,
  sidebarState,
  i18nLocale,
}: GetOrganizationDropdownOptionsProps): MenuItemDropdownOption[] => {
  const otherOrganizations = currentUserOrganizations.filter(
    org => org._id !== organizationId
  );
  const organizationsSubMenu = buildOrganizationsSubMenu(
    otherOrganizations,
    sidebarState,
    i18nLocale
  );
  const settingsSubMenu = [
    {
      label: t(
        i18nLocale,
        'navigationTopBar:organizationSettings',
        'Organization settings'
      ),
      // User is an admin
      shouldHide: orgAccessLevel !== OrgAccessLevel.ADMIN,
      isActive: activeMenuItem === AppModules.MANAGE_ORGANIZATION,
      ...toLinkProps(
        { route: '/manage-organization/settings', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Organization settings', sidebarState },
          },
        }
      ),
    },
    {
      label: t(
        i18nLocale,
        'navigationTopBar:organizationDocuments',
        'Organization documents'
      ),
      // User is a writer without graph filter permissions
      shouldHide: !(
        orgAccessLevel === OrgAccessLevel.WRITER && !canCreateGraphFilter
      ),
      isActive: activeMenuItem === AppModules.MANAGE_ORGANIZATION,
      ...toLinkProps(
        { route: '/manage-organization/document-archive', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Organization settings', sidebarState },
          },
        }
      ),
    },
    {
      label: t(
        i18nLocale,
        'navigationTopBar:organizationSettings',
        'Organization settings'
      ),
      // User is a writer with graph filter permissions
      shouldHide: !(
        orgAccessLevel === OrgAccessLevel.WRITER && canCreateGraphFilter
      ),
      isActive: activeMenuItem === AppModules.MANAGE_ORGANIZATION,
      ...toLinkProps(
        { route: '/manage-organization/graph-filters', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Organization settings', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:showAdmins', 'Show admins'),
      shouldHide:
        orgAccessLevel === OrgAccessLevel.ADMIN ||
        orgAccessLevel === OrgAccessLevel.CONTRIBUTOR,
      onClick: () => {
        dispatchAction(
          orgAdminsListActions.openDialog({
            [trackingData]: {
              event: 'Sidebar0 Menu item clicked',
              payload: { label: 'Show admins', sidebarState },
            },
          })
        );
      },
    },
    {
      label: t(i18nLocale, 'navigationTopBar:accessControl', 'Access control'),
      shouldHide: orgAccessLevel !== OrgAccessLevel.ADMIN,
      isActive: activeMenuItem === AppModules.ACCESS_CONTROL_V2,
      ...toLinkProps(
        { route: accessControlRoutes.usersList, params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Access control', sidebarState },
          },
        }
      ),
    },
    {
      label: '',
      type: DropdownOptionType.DIVIDER,
      shouldHide: orgAccessLevel !== OrgAccessLevel.ADMIN,
    },
    {
      label: t(i18nLocale, 'navigationTopBar:usageMetrics', 'Usage metrics'),
      shouldHide: !hasUsageMetrics || orgAccessLevel !== OrgAccessLevel.ADMIN,
      ...toLinkProps(
        { route: '/dashboard/usage-metrics', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Usage metrics', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:metamodel', 'Metamodel'),
      ...toLinkProps(toMetamodelHomeLocation(), {
        trackingEvent: {
          event: 'Sidebar0 Menu item clicked',
          payload: { label: 'Metamodel', sidebarState },
        },
      }),
      shouldHide:
        !resourcePermissionsOperations.canManageGlobalFields(
          permissionContext
        ) && orgAccessLevel !== OrgAccessLevel.ADMIN,
    },
  ].filter(option => !option.shouldHide);

  return [
    {
      label: t(
        i18nLocale,
        'navigationTopBar:myOrganization',
        'MY ORGANIZATION'
      ),
      type: DropdownOptionType.HEADER,
    },
    {
      label: organizationName,
      iconName: IconName.COMPONENT,
      ...(organizationsSubMenu.length > 0 && { subMenu: organizationsSubMenu }),
    },
    { label: '', type: DropdownOptionType.DIVIDER },
    {
      label: t(i18nLocale, 'navigationTopBar:helpSupport', 'Help & support'),
      isActive: activeMenuItem === AppModules.HELP_AND_SUPPORT,
      ...toLinkProps(
        { route: '/help-and-support', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Help & support', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:chatWithUs', 'Chat with us'),
      iconName: IconName.CUSTOMER_SERVICE,
      shouldHide: !hasAccessToSupportChat,
      onClick: () => {
        trackEventV2({
          event: 'Sidebar0 Menu item clicked',
          payload: { label: 'Chat with us', sidebarState },
        });
        openChatWithUs();
      },
    },
    { label: '', type: DropdownOptionType.DIVIDER },
    {
      label: t(i18nLocale, 'navigationTopBar:auditLog', 'Audit log'),
      isActive: activeMenuItem === AppModules.AUDIT_LOG,
      shouldHide: !doesUserHaveAccessToFullCoreApp,
      ...toLinkProps(
        { route: '/audit-log', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Audit log', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:myAccount', 'My account'),
      iconName: IconName.MY_ACCOUNT,
      ...toLinkProps(
        { route: '/account-settings/profile', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'My account', sidebarState },
          },
        }
      ),
    },
    {
      label: t(i18nLocale, 'navigationTopBar:admin', 'Admin'),
      iconName: IconName.ADMIN,
      shouldHide: settingsSubMenu.length < 1,
      subMenu: settingsSubMenu,
    },
    { label: '', type: DropdownOptionType.DIVIDER },
    {
      label: t(i18nLocale, 'navigationTopBar:logOut', 'Log out'),
      iconName: IconName.LOG_OUT,
      onClick: () => {
        trackEventV2({
          event: 'Sidebar0 Menu item clicked',
          payload: { label: 'Log out', sidebarState },
        });
        logout();
      },
    },
  ].filter(option => !option.shouldHide);
};

type GetDiscoverInCoreOptionsProps = {
  activeMenuItem?: ActiveMenuItem;
  doesUserHaveAccessToFullCoreApp: boolean;
  orgHasDiscoverInCore: boolean;
  userHasDiscoverInCorePrivilege: boolean;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
  isInDiscoverScope: boolean;
};

export const getDiscoverInCoreOptions = ({
  activeMenuItem,
  doesUserHaveAccessToFullCoreApp,
  orgHasDiscoverInCore,
  userHasDiscoverInCorePrivilege,
  sidebarState,
  i18nLocale,
  isInDiscoverScope,
}: GetDiscoverInCoreOptionsProps) => {
  // Contributors can access Discover if the org has it ofc, but for them it's the home button
  if (
    !doesUserHaveAccessToFullCoreApp ||
    !orgHasDiscoverInCore ||
    !userHasDiscoverInCorePrivilege ||
    isInDiscoverScope
  ) {
    return [];
  }
  return [
    {
      label: t(i18nLocale, 'navigationTopBar:discover', 'Discover'),
      iconName: SvgImageName.INVERTED_ARDOQ_LOGO,
      isActive: activeMenuItem === AppModules.DISCOVER_LANDING_PAGE,
      ...toLinkProps(
        { route: '/discover', params: {} },
        {
          trackingEvent: {
            event: 'Sidebar0 Menu item clicked',
            payload: { label: 'Discover', sidebarState },
          },
        }
      ),
    },
  ];
};

type GetBottomSectionLinksOptionsProps = {
  userCanOpenLegacyDiscover: boolean;
  orgHasDiscoverInCore: boolean;
  hasShiftXFeature: boolean;
  sidebarState: SidebarState;
  i18nLocale: I18nLocale;
};

export const getBottomSectionLinks = ({
  userCanOpenLegacyDiscover,
  orgHasDiscoverInCore,
  hasShiftXFeature,
  sidebarState,
  i18nLocale,
}: GetBottomSectionLinksOptionsProps): MenuItemExternalLinkProps[] => {
  const options = [
    {
      label: t(i18nLocale, 'navigationTopBar:discover', 'Discover'),
      iconName: SvgImageName.INVERTED_ARDOQ_LOGO,
      href: '/discover',
      shouldHide: !userCanOpenLegacyDiscover || orgHasDiscoverInCore,
    },
    {
      label: t(i18nLocale, 'navigationTopBar:shiftX', 'ShiftX'),
      iconName: IconName.SHIFTX,
      href: '/shiftx',
      shouldHide: !hasShiftXFeature,
      onClick: () => {
        trackEventV2({
          event: 'Sidebar0 Menu item clicked',
          payload: { label: 'ShiftX', sidebarState },
        });
        window.open(withBasePath('/shiftx'), '_blank', 'noopener,noreferrer');
      },
    },
  ];

  return options.filter(option => !option.shouldHide);
};
// this is the gap of the flex content between the filter bar (16) -> table -> pagination (8)
const PAGE_CONTENT_GAPPING = 26;

export const getAssetBrowserHeight = (
  hasNewTopbarAndSidebar: boolean,
  height?: number
) => {
  if (!hasNewTopbarAndSidebar) {
    return height ? height - PAGE_TOPBAR_HEIGHT : 0;
  }
  return height ? height - (NEW_PAGE_TOPBAR_HEIGHT + PAGE_CONTENT_GAPPING) : 0;
};

const filtersFunctions: Record<
  NotificationsFilterType,
  (n: NotificationCardProps) => boolean
> = {
  [NotificationsFilterType.ALL]: () => true,
  [NotificationsFilterType.UNREAD]: ({ isUnread }) => isUnread,
};

export const toNotificationsMenuProps = ({
  notificationsStreamState,
  sidebarState,
  isNotificationsMenuOpen,
  i18nLocale,
}: {
  notificationsStreamState: InAppNotificationsState;
  sidebarState: SidebarState;
  isNotificationsMenuOpen: boolean;
  i18nLocale: I18nLocale;
}): NotificationsMenuProps => {
  const {
    notifications,
    unreadNotificationIds,
    filteringBy,
    isLoading: isLoadingNotifications,
    checksCounter,
  } = notificationsStreamState;
  const filteredNotifications = notifications
    .sort(notificationOperations.sortNewestFirst)
    .filter(filtersFunctions[filteringBy]);

  const unreadNotificationsCount = unreadNotificationIds.length;
  const isLoading =
    isLoadingNotifications && checksCounter === ChecksCounter.ZERO;

  return {
    isLoading,
    unreadNotificationsCount,
    filteringBy,
    notifications: filteredNotifications,
    isRefreshing: isLoading && checksCounter !== ChecksCounter.ZERO,
    showEmptyState: !isLoading && filteredNotifications.length === 0,
    isMenuSidebarOpened: sidebarState === 'open',
    isOpen: isNotificationsMenuOpen,
    i18nLocale,
    commands: {
      closeNotificationsMenu: () => dispatchAction(closeNotificationsMenu()),
      selectFilterType: (filterType: NotificationsFilterType) =>
        dispatchAction(filterSelected(filterType)),
      openNavigationMenu: () =>
        navigationTopBarCommands.openSidebar({
          event: 'TopBar0 Toggled sidebar',
          payload: { state: 'open' },
        }),
    },
  };
};
